/**
 * CreateEntityModal
 * Modal voor het aanmaken van nieuwe entities (clubs, masterminds, communities)
 */

import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { collection, addDoc, doc, setDoc, serverTimestamp, getDoc, query, where, getDocs } from 'firebase/firestore';
import { EntityType } from '../../types/entityManagement.types';
import { networkClubService } from '../../services/networkClubService';
import { Icon } from '../icons';
import { Building2, X, Loader, Globe, Users, Briefcase, AlertTriangle } from '../icons';
import {
  ThinkIcon, CommunityIcon, PartnerIcon,
} from '../icons';
import { 
  getEntityAccessRights, 
  canCreateEntity, 
  getEntityDescription,
  getSubscriptionTier,
  canBePublic
} from '../../utils/subscriptionUtils';

interface CreateEntityModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateEntityModal: React.FC<CreateEntityModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: 'networkclub' as EntityType,
    name: '',
    description: '',
    visibility: 'public' as 'public' | 'private',
    scope: 'lokaal' as 'lokaal' | 'regionaal' | 'nationaal' | 'internationaal',
    region: '',
    city: '',
    kvkNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [currentEntityCount, setCurrentEntityCount] = useState(0);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  // Load user subscription and entity count
  useEffect(() => {
    const loadUserData = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        // Load subscription
        const userDoc = await getDoc(doc(db, 'userProfiles', user.uid));
        if (userDoc.exists()) {
          setUserSubscription(userDoc.data().subscription);
        }

        // Count existing entities
        const entitiesSnapshot = await getDocs(
          query(collection(db, 'entities'), where('ownerId', '==', user.uid))
        );
        setCurrentEntityCount(entitiesSnapshot.size);
      } catch (err) {
        console.error('Error loading user data:', err);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadUserData();
  }, []);

  const subscriptionTier = getSubscriptionTier(userSubscription);
  const accessRights = getEntityAccessRights(subscriptionTier, formData.type as any);
  const entityCreationCheck = canCreateEntity(subscriptionTier, currentEntityCount);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Naam is verplicht');
      return;
    }

    // Check if user can create entity
    if (!entityCreationCheck.allowed) {
      setError(entityCreationCheck.reason || 'Je hebt het maximum aantal entities bereikt');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('Je moet ingelogd zijn');
        setLoading(false);
        return;
      }

      // Public is always allowed — no subscription required
      const canMakePublic = canBePublic(formData.type as any);
      const isPublic = canMakePublic && formData.visibility === 'public';

      // Maak entity aan
      const entityData: any = {
        type: formData.type,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        ownerId: user.uid,
        ownerName: user.displayName || undefined,
        ownerEmail: user.email || undefined,
        
        // Visibility & Access Control
        isPublic: isPublic,
        visibility: isPublic ? 'public' : 'private',
        requiresSubscription: false,
        ownerHasSubscription: subscriptionTier !== 'free',
        
        // Members
        memberCount: 1,
        maxMembers: accessRights.maxMembers,
        
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Type-specifieke velden
      if (formData.type === 'networkclub' && formData.kvkNumber) {
        entityData.kvkNumber = formData.kvkNumber;
        entityData.isKvkValidated = false;
      }

      const entityRef = await addDoc(collection(db, 'entities'), entityData);
      const entityId = entityRef.id;

      // Maak entityMember aan
      await setDoc(doc(db, 'entityMembers', `${entityId}_${user.uid}`), {
        entityId: entityId,
        userId: user.uid,
        role: formData.type === 'networkclub' ? 'organizer' : 'owner',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
      });

      // Als networkclub, maak ook networkClub document aan
      if (formData.type === 'networkclub') {
        const clubData: any = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          type: formData.type,
          visibility: formData.visibility,
          scope: formData.scope,
          region: formData.region || undefined,
          city: formData.city || undefined,
          country: 'Nederland',
          kvkNumber: formData.kvkNumber || undefined,
          entityId: entityId,
          organizerId: user.uid,
          organizerName: user.displayName || undefined,
          organizerEmail: user.email || undefined,
          isActive: false,
          isVisible: false,
          memberCount: 1,
          minMembersRequired: 5,
          moderationStatus: 'pending',
          isVerified: false,
          isKvkValidated: false
        };

        await networkClubService.addNetworkClub(clubData);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error creating entity:', err);
      setError(err.message || 'Fout bij aanmaken van entiteit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Icon icon={Building2} size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-bold">Nieuwe Entiteit Aanmaken</h3>
                <p className="text-white/80 text-sm">Maak een club, mastermind of community aan</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Icon icon={X} size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Subscription Warning - Kan geen entity aanmaken */}
          {!entityCreationCheck.allowed && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon icon={AlertTriangle} size={20} strokeWidth={1.5} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">Maximum bereikt</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {entityCreationCheck.reason}
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Je hebt {currentEntityCount}/{entityCreationCheck.maxEntities} entities aangemaakt
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'networkclub' })}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  formData.type === 'networkclub'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mb-2">
                  <PartnerIcon size={18} weight="regular" className="text-gray-600" />
                </div>
                <div className="font-medium text-sm">Netwerkclub</div>
                <div className="text-xs text-gray-500 mt-1">{getEntityDescription('networkclub')}</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'mastermind' })}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  formData.type === 'mastermind'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mb-2">
                  <ThinkIcon size={18} weight="regular" className="text-gray-600" />
                </div>
                <div className="font-medium text-sm">Mastermind</div>
                <div className="text-xs text-gray-500 mt-1">{getEntityDescription('mastermind')}</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'community' })}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  formData.type === 'community'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mb-2">
                  <CommunityIcon size={18} weight="regular" className="text-gray-600" />
                </div>
                <div className="font-medium text-sm">Community</div>
                <div className="text-xs text-gray-500 mt-1">{getEntityDescription('community')}</div>
              </button>
            </div>
          </div>

          {/* Subscription Warning - Free user — removed, public is always allowed */}

          {/* Naam */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Naam <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Naam van de entiteit"
              required
            />
          </div>

          {/* Beschrijving */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschrijving
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              rows={3}
              placeholder="Korte beschrijving..."
            />
          </div>

          {/* KVK (alleen voor networkclub) */}
          {formData.type === 'networkclub' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                KVK Nummer
              </label>
              <input
                type="text"
                value={formData.kvkNumber}
                onChange={(e) => setFormData({ ...formData, kvkNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="12345678"
                maxLength={8}
              />
            </div>
          )}

          {/* Visibility */}
          {canBePublic(formData.type as any) ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zichtbaarheid
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, visibility: 'public' })}
                  className={`p-3 border-2 rounded-lg transition-colors ${
                    formData.visibility === 'public'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm">🌍 Openbaar</div>
                  <div className="text-xs text-gray-600 mt-1">Iedereen kan vinden & aanmelden</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, visibility: 'private' })}
                  className={`p-3 border-2 rounded-lg transition-colors ${
                    formData.visibility === 'private'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm">🔒 Besloten</div>
                  <div className="text-xs text-gray-600 mt-1">Alleen uitgenodigd</div>
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">🔒 Zichtbaarheid:</span>
                <span className="text-sm text-gray-600">Besloten (Masterminds zijn altijd privé)</span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={loading || !entityCreationCheck.allowed || loadingSubscription}
              className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
            >
              {loading || loadingSubscription ? (
                <>
                  <Icon icon={Loader} size={16} strokeWidth={1.5} className="animate-spin" />
                  <span>{loadingSubscription ? 'Laden...' : 'Aanmaken...'}</span>
                </>
              ) : (
                <span>
                  {!entityCreationCheck.allowed ? 'Maximum bereikt' : 'Aanmaken'}
                </span>
              )}
            </button>
          </div>
          
          {/* Info over wat er gebeurt */}
          {entityCreationCheck.allowed && (
            <div className="pt-2 px-1">
              <p className="text-xs text-gray-500 text-center">
                {formData.visibility === 'public'
                  ? `✅ Je ${formData.type} wordt publiekelijk zichtbaar en vindbaar`
                  : `🔒 Je ${formData.type} blijft besloten (alleen uitgenodigde leden)`
                }
              </p>
              {!accessRights.canInviteMembers && (
                <p className="text-xs text-amber-600 text-center mt-1">
                  💡 Upgrade om leden uit te nodigen
                </p>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateEntityModal;
